import Decimal from 'decimal.js';
import { RefObject, useCallback, useEffect } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Loader } from '@/components/progress/Loader/Loader';
import { BeneficiaryDesignationsModalBody } from '@/modules/beneficiaryDesignations/BeneficiaryDesignationsModalBody';
import { useDispositiveDiagramSyncContext } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { isBeneficiaryDesignationEntityKind } from '@/modules/dispositiveProvisions/dispositiveProvisions.label';
import { DispositiveProvisionsModalBody } from '@/modules/dispositiveProvisions/DispositiveProvisionsSplitScreenModal/DispositiveProvisionsModalBody';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { useEditEntitySplitScreenModalContext } from '@/modules/entities/EditEntitySplitScreen/contexts/editEntitySplitScreenModal.context';
import { getGrantorIdForSection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.utils';
import { EditEntitySplitScreenForm } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenForm';
import { EntityType } from '@/modules/entities/types/EntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { getTileEntityMetadata } from '@/modules/entityDiagram/getTileEntityMetadata';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { diagnostics } from '@/utils/diagnostics';

import { EditEntitySection } from './EditEntitySplitScreen.types';

export function EditEntitySplitScreenRightContent() {
  const { showFeedback } = useFeedback();

  const {
    currentSection,
    headerRef,
    footerRef,
    initialSubformValues,
    entityValueOverride,
    hasError,
    handleCloseModal,
    isDPSection,
    isBDGSection,
    entityType,
    setCurrentSection,
    onAfterSave,
  } = useEditEntitySplitScreenModalContext();

  const { primaryClients } = useHouseholdDetailsContext();
  const { resetDiagramSyncState } = useDispositiveDiagramSyncContext();

  useEffect(() => {
    if (!hasError) return;
    showFeedback(
      'Could not load form data. Please refresh the page and try again.'
    );
  }, [hasError, showFeedback]);

  const handleAfterSubmit = useCallback(
    (afterSubmitSection?: EditEntitySection) => {
      diagnostics.debug(
        '[EditEntitySplitScreenRightContent][handleAfterSubmit]',
        {
          afterSubmitSection,
        }
      );
      if (afterSubmitSection) {
        // DispositiveDiagramSyncProvider persists at a higher level than the
        // keyed DispositiveProvisionsModalBody, so its state must be reset
        // before switching sections or the next diagram reads the previous
        // section's overlays (e.g. recipientDispositions resolved for the
        // previous death order).
        resetDiagramSyncState();
        setCurrentSection(afterSubmitSection);
      } else {
        handleCloseModal();
      }
    },
    [handleCloseModal, setCurrentSection, resetDiagramSyncState]
  );

  if (isBDGSection && entityType) {
    return (
      <BeneficiaryDesignationsSectionContent
        entityType={entityType}
        onClose={handleCloseModal}
        onAfterSubmit={handleAfterSubmit}
        onDidSave={onAfterSave}
      />
    );
  }

  if (isDPSection) {
    const firstDeathClientId = getGrantorIdForSection(
      currentSection,
      primaryClients
    );

    return (
      <DispositiveProvisionsSectionContent
        currentSection={currentSection}
        headerRef={headerRef}
        footerRef={footerRef}
        entityValueOverride={entityValueOverride}
        onClose={handleCloseModal}
        onAfterSubmit={handleAfterSubmit}
        onDidSave={onAfterSave}
        firstDeathClientId={firstDeathClientId}
      />
    );
  }

  if (!initialSubformValues) {
    // Loading state for the Entity Forms
    return (
      <Loader
        boxProps={{
          sx: {
            textAlign: 'center',
            my: 3,
          },
        }}
      />
    );
  }

  // Only initialize Entity Form's FormProvider/Context once we've finished
  // loading the data. This fixes a bug where we overwrite the initialized
  // FormRepeaters with empty data.
  return (
    <EditEntitySplitScreenForm initialSubformValues={initialSubformValues} />
  );
}

interface BeneficiaryDesignationsSectionContentProps {
  entityType: EntityType;
  onClose: () => void;
  onAfterSubmit: (afterSubmitSection?: EditEntitySection) => void;
  onDidSave?: () => void;
}

/**
 * Routes the right-hand pane for a BDG entry section. Root → BDG form for
 * the root entity. Non-root clicks defer to {@link NonRootTargetSwitchedBody}
 * which picks BDG form vs DP form based on the clicked tile's kind.
 */
function BeneficiaryDesignationsSectionContent({
  entityType,
  onClose,
  onAfterSubmit,
  onDidSave,
}: BeneficiaryDesignationsSectionContentProps) {
  const { entityId } = useEntityDetailsContext();
  const { committedTargetNode } = useDispositiveDiagramSyncContext();

  const isOnRootTarget =
    !committedTargetNode || committedTargetNode.pulid === entityId;

  if (isOnRootTarget) {
    return (
      <BeneficiaryDesignationsModalBody
        key="beneficiary-designations"
        onClose={onClose}
        onAfterSubmit={onAfterSubmit}
        onDidSave={onDidSave}
        entityType={entityType}
      />
    );
  }

  return (
    <NonRootTargetSwitchedBody
      onClose={onClose}
      onAfterSubmit={onAfterSubmit}
      onDidSave={onDidSave}
    />
  );
}

interface DispositiveProvisionsSectionContentProps {
  currentSection: EditEntitySection;
  headerRef: RefObject<HTMLDivElement | null>;
  footerRef: RefObject<HTMLDivElement | null>;
  entityValueOverride: Decimal | undefined;
  onClose: () => void;
  onAfterSubmit: (afterSubmitSection?: EditEntitySection) => void;
  onDidSave?: () => void;
  firstDeathClientId: string;
}

/**
 * Routes the right-hand pane for a DP entry section. Root → DP form for the
 * root entity. Non-root clicks defer to {@link NonRootTargetSwitchedBody}
 * which picks BDG form vs DP form based on the clicked tile's kind.
 */
function DispositiveProvisionsSectionContent({
  currentSection,
  headerRef,
  footerRef,
  entityValueOverride,
  onClose,
  onAfterSubmit,
  onDidSave,
  firstDeathClientId,
}: DispositiveProvisionsSectionContentProps) {
  const { entityId } = useEntityDetailsContext();
  const { committedTargetNode } = useDispositiveDiagramSyncContext();

  const isOnRootTarget =
    !committedTargetNode || committedTargetNode.pulid === entityId;

  if (!isOnRootTarget) {
    return (
      <NonRootTargetSwitchedBody
        onClose={onClose}
        onAfterSubmit={onAfterSubmit}
        onDidSave={onDidSave}
        dpFallbackFirstDeathClientId={firstDeathClientId}
      />
    );
  }

  // This is responsible for showing its own loading state
  return (
    <DispositiveProvisionsModalBody
      key={currentSection} // force the component to re-render when the section changes
      headerRef={headerRef}
      footerRef={footerRef}
      nodeValue={entityValueOverride}
      onClose={onClose}
      onSwitchSectionAfterSubmit={onAfterSubmit}
      onDidSave={onDidSave}
      variant="editEntity"
      firstDeathClientId={firstDeathClientId}
    />
  );
}

interface NonRootTargetSwitchedBodyProps {
  onClose: () => void;
  onAfterSubmit: (afterSubmitSection?: EditEntitySection) => void;
  onDidSave?: () => void;
  /**
   * `firstDeathClientId` to pass to the DP fallback body. Provided by a DP
   * root section (derived from the active first/second-death section) so
   * the DP body loads the correct death's data when a non-root DP tile is
   * clicked. Omitted from BDG root sections, where we fall back to the
   * BDG diagram's selected primary client.
   */
  dpFallbackFirstDeathClientId?: string;
}

/**
 * Right-pane body when a non-root tile is selected. Reads the clicked
 * tile's stashed entity metadata; if the tile is a BDG-backed
 * retirement/insurance entity (and the FF is on) renders the BDG form for
 * it; otherwise falls back to the DP modal body which loads the clicked
 * target's DP data via its internal lazy query.
 */
function NonRootTargetSwitchedBody({
  onClose,
  onAfterSubmit,
  onDidSave,
  dpFallbackFirstDeathClientId,
}: NonRootTargetSwitchedBodyProps) {
  const { primaryClients } = useHouseholdDetailsContext();
  const { headerRef, footerRef } = useEditEntitySplitScreenModalContext();
  const { committedTargetNode, bdgDiagramSyncState } =
    useDispositiveDiagramSyncContext();

  const meta = getTileEntityMetadata(committedTargetNode?.node ?? null);
  const isBdgTarget = isBeneficiaryDesignationEntityKind(meta.entityKind);

  const firstDeathClientId =
    dpFallbackFirstDeathClientId ??
    bdgDiagramSyncState?.selectedPrimaryClientId ??
    primaryClients?.[0]?.id ??
    '';

  if (isBdgTarget && committedTargetNode?.pulid && meta.entityKind) {
    const targetEntityType = getEntityTypeFromEntityKind(meta.entityKind);
    if (
      targetEntityType === 'retirement-account' ||
      targetEntityType === 'insurance-account'
    ) {
      return (
        <BeneficiaryDesignationsModalBody
          key={`bdg-target:${committedTargetNode.pulid}`}
          onClose={onClose}
          onAfterSubmit={onAfterSubmit}
          onDidSave={onDidSave}
          entityType={targetEntityType}
          entityIdOverride={committedTargetNode.pulid}
          displayNameOverride={meta.displayName ?? undefined}
          firstDeathClientIdOverride={firstDeathClientId || undefined}
        />
      );
    }
  }

  return (
    <DispositiveProvisionsModalBody
      key={`non-root-dp:${committedTargetNode?.pulid ?? 'none'}`}
      headerRef={headerRef}
      footerRef={footerRef}
      onClose={onClose}
      onSwitchSectionAfterSubmit={onAfterSubmit}
      onDidSave={onDidSave}
      variant="editEntity"
      firstDeathClientId={firstDeathClientId}
    />
  );
}
